import { Check } from 'lucide-react';
import { useState } from 'react';
import { StateFrame } from '@/components/layout/workspace/StateFrame';
import { useWorkspace } from '@/components/layout/workspace/workspaceContext';
import { useToast } from '@/components/ui';
import { HOME } from '../model';
import { useShellCommands } from '../shellCommands';

/*
  Состояния вкладки целиком в «Основе» (R42): адреса нет, объекта нет, нет доступа. Вид — StateFrame:
  медальон знака, заголовок, что делать дальше. Тревоги и смены тона нет: это не сбой, и знак в меню
  ничего не показывает (решение 21.10). CrashProbe — проба «Уронить вкладку»: ошибку ловит настоящая
  граница вкладки.
*/

/** Проба «Уронить вкладку»: взведена — бросает исключение при отрисовке, как настоящая ошибка вида. */
export function CrashProbe({ armed }: { armed: boolean }) {
  if (armed) throw new TypeError('Проба: вкладку уронили кнопкой «Уронить вкладку» на верхней полосе');
  return null;
}

/** Неизвестный адрес. */
export function NotFoundScreen() {
  const { go } = useWorkspace();
  const { openSearch } = useShellCommands();
  return (
    <StateFrame
      kind="missing"
      title="Такой страницы нет"
      description="Адрес мог устареть, или в нём опечатка."
      action={{ label: 'На главную', onClick: () => go(HOME) }}
      secondaryAction={{ label: 'Найти · Ctrl+K', onClick: openSearch }}
    />
  );
}

/** Объект по адресу не нашёлся: узел, проблема, модуль. Первое действие — к его списку. */
export function ObjectNotFound({
  title,
  description,
  list,
  find = 'Найти · Ctrl+K',
}: {
  title: string;
  description: string;
  /** Список, к которому относится объект (его вид). */
  list: { label: string; path: string };
  find?: string;
}) {
  const { go } = useWorkspace();
  const { openSearch } = useShellCommands();
  return (
    <StateFrame
      kind="missing"
      title={title}
      description={description}
      action={{ label: list.label, onClick: () => go(list.path) }}
      secondaryAction={{ label: find, onClick: openSearch }}
    />
  );
}

/**
 * Нет доступа: раздел есть, но для этой роли закрыт. «Запросить доступ» срабатывает сразу, с отменой
 * в тосте и Ctrl+Z (R39); «Назад» — туда, откуда пришли.
 */
export function NoAccessScreen({ name, object = false }: { name: string; object?: boolean }) {
  const { go } = useWorkspace();
  const { toast } = useToast();
  const [requested, setRequested] = useState(false);
  const back = () => {
    const idx = (window.history.state as { idx?: number } | null)?.idx;
    if (typeof idx === 'number' && idx > 0) window.history.back();
    else go(HOME);
  };
  return (
    <StateFrame
      kind="locked"
      title={object ? `Нет доступа к «${name}»` : `Нет доступа к разделу «${name}»`}
      description={`${object ? 'Объект' : 'Раздел'} есть, но для этой роли закрыт. Доступ выдаёт владелец приложения.`}
      action={
        requested
          ? { label: 'Запрос отправлен', icon: <Check />, variant: 'secondary', disabled: true }
          : {
              label: 'Запросить доступ',
              variant: 'primary',
              onClick: () => {
                setRequested(true);
                toast({
                  title: 'Запрос отправлен владельцу',
                  action: { label: 'Отменить', undo: true, onClick: () => setRequested(false) },
                });
              },
            }
      }
      secondaryAction={{ label: 'Назад', onClick: back }}
    />
  );
}
